import React, { useEffect, useRef, useState } from 'react';
import { FileText as FileIcon, Volume2, X } from 'lucide-react';

const pdfWorkerSrc = new URL(
  'pdfjs-dist/legacy/build/pdf.worker.min.mjs',
  import.meta.url
).toString();

const getAttachmentLabel = (attachment) => attachment?.name || 'Attachment';

const decodeDataUrl = (dataUrl) => {
  const [, payload = ''] = dataUrl.split(',');
  const binary = window.atob(payload);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
};

const getPdfDocumentSource = async (attachment) => {
  if (attachment?.file?.arrayBuffer) {
    const buffer = await attachment.file.arrayBuffer();
    return { data: new Uint8Array(buffer) };
  }

  const source = attachment?.previewUrl || attachment?.url;
  if (!source) return null;
  if (source.startsWith('data:')) return { data: decodeDataUrl(source) };
  return { url: source };
};

const StaticPdfPreviewArtwork = () => (
  <div className="h-full w-full space-y-1.5 p-2">
    <div className="h-1.5 w-16 rounded-full bg-gray-500/35" />
    <div className="h-1 w-20 rounded-full bg-gray-400/45" />
    <div className="h-1 w-16 rounded-full bg-gray-400/35" />
    <div className="pt-1 space-y-1">
      <div className="h-1 w-full rounded-full bg-gray-300" />
      <div className="h-1 w-11/12 rounded-full bg-gray-300" />
      <div className="h-1 w-full rounded-full bg-gray-300" />
      <div className="h-1 w-9/12 rounded-full bg-gray-300" />
      <div className="h-1 w-full rounded-full bg-gray-300" />
      <div className="h-1 w-10/12 rounded-full bg-gray-300" />
      <div className="h-1 w-full rounded-full bg-gray-300" />
      <div className="h-1 w-8/12 rounded-full bg-gray-300" />
    </div>
    <div className="grid grid-cols-2 gap-1 pt-1">
      <div className="h-6 rounded-sm bg-gray-100" />
      <div className="space-y-1">
        <div className="h-1 w-full rounded-full bg-gray-300" />
        <div className="h-1 w-10/12 rounded-full bg-gray-300" />
        <div className="h-1 w-full rounded-full bg-gray-300" />
      </div>
    </div>
  </div>
);

const PdfCanvasPreview = ({ attachment }) => {
  const canvasRef = useRef(null);
  const [isRendered, setIsRendered] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let loadingTask = null;

    const renderPreview = async () => {
      setIsRendered(false);
      try {
        const documentSource = await getPdfDocumentSource(attachment);
        if (!documentSource || !canvasRef.current) return;

        const pdfjs = await import('pdfjs-dist/legacy/build/pdf.mjs');
        pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerSrc;
        loadingTask = pdfjs.getDocument({
          ...documentSource,
          disableAutoFetch: true,
          disableFontFace: true,
          disableStream: true,
        });

        const pdf = await loadingTask.promise;
        const page = await pdf.getPage(1);
        if (cancelled || !canvasRef.current) {
          pdf.destroy?.();
          return;
        }

        const targetWidth = 104;
        const targetHeight = 112;
        const pixelRatio = window.devicePixelRatio || 1;
        const baseViewport = page.getViewport({ scale: 1 });
        const cssScale = Math.min(
          targetWidth / baseViewport.width,
          targetHeight / baseViewport.height
        );
        const viewport = page.getViewport({ scale: cssScale * pixelRatio });
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');

        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        canvas.style.width = `${Math.floor(viewport.width / pixelRatio)}px`;
        canvas.style.height = `${Math.floor(viewport.height / pixelRatio)}px`;

        await page.render({ canvasContext: context, viewport }).promise;
        if (!cancelled) setIsRendered(true);
        pdf.destroy?.();
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn('PDF preview render failed', error);
        }
        if (!cancelled) setIsRendered(false);
      }
    };

    renderPreview();

    return () => {
      cancelled = true;
      loadingTask?.destroy?.();
    };
  }, [attachment]);

  return (
    <div className="absolute inset-2 flex items-start justify-center overflow-hidden rounded-[14px] border border-gray-200 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.10)] dark:border-gray-300">
      {!isRendered && <StaticPdfPreviewArtwork />}
      <canvas
        ref={canvasRef}
        className={isRendered ? 'block bg-white' : 'hidden'}
        aria-hidden="true"
      />
    </div>
  );
};

const FilePreview = ({
  attachments = [],
  onRemove,
  onClearAll,
  onImageClick,
  embedded = false,
}) => {
  if (!attachments.length) {
    return null;
  }

  const renderRemoveButton = (attachmentId) => (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onRemove && onRemove(attachmentId);
      }}
      className="absolute right-1.5 top-1.5 bg-gray-800 bg-opacity-60 text-white rounded-full p-1 hover:bg-opacity-90 transition-colors duration-200"
      aria-label="Remove attachment"
    >
      <X size={14} />
    </button>
  );

  const renderImageAttachment = (attachment) => {
    const previewSource = attachment.previewUrl || attachment.url;
    return (
      <div
        key={attachment.id}
        className="relative overflow-hidden rounded-[22px] border border-gray-200 dark:border-gray-700 cursor-pointer group"
        role="button"
        tabIndex={0}
        onClick={(e) =>
          onImageClick && onImageClick(previewSource, getAttachmentLabel(attachment), e.target)
        }
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && onImageClick) {
            e.preventDefault();
            onImageClick(previewSource, getAttachmentLabel(attachment), e.target);
          }
        }}
      >
        <img
          src={previewSource}
          alt={getAttachmentLabel(attachment)}
          className="h-32 w-32 object-cover transition-transform duration-200 group-hover:scale-105"
        />
        {renderRemoveButton(attachment.id)}
      </div>
    );
  };

  const renderPdfAttachment = (attachment) => (
    <div
      key={attachment.id}
      className="relative h-32 w-32 overflow-hidden rounded-[22px] border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900"
      aria-label={getAttachmentLabel(attachment)}
    >
      <PdfCanvasPreview attachment={attachment} />
      <span className="absolute bottom-2 left-2 rounded-md border border-gray-300 bg-white px-2 py-1 text-xs font-semibold leading-none text-gray-700 shadow-sm dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100">
        PDF
      </span>
      {renderRemoveButton(attachment.id)}
    </div>
  );

  const renderAudioAttachment = (attachment) => {
    const previewSource = attachment.previewUrl || attachment.url;
    return (
      <div
        key={attachment.id}
      className="relative flex flex-col gap-2 p-3 w-56 bg-gray-100 dark:bg-gray-800 rounded-[22px] border border-gray-200 dark:border-gray-700"
      >
        <div className="flex items-center gap-2">
          <Volume2 size={18} className="text-gray-600 dark:text-gray-300" />
          <span className="text-sm text-gray-700 dark:text-gray-300 truncate">
            {getAttachmentLabel(attachment)}
          </span>
        </div>
        <audio controls className="w-full">
          <source src={previewSource} type={attachment.mimeType || 'audio/mpeg'} />
          Your browser does not support audio playback.
        </audio>
        {renderRemoveButton(attachment.id)}
      </div>
    );
  };

  const renderVideoAttachment = (attachment) => {
    const previewSource = attachment.previewUrl || attachment.url;
    return (
      <div
        key={attachment.id}
      className="relative flex flex-col gap-2 p-3 w-56 bg-gray-100 dark:bg-gray-800 rounded-[22px] border border-gray-200 dark:border-gray-700"
      >
        <div className="flex items-center gap-2">
          <FileIcon size={18} className="text-gray-600 dark:text-gray-300" />
          <span className="text-sm text-gray-700 dark:text-gray-300 truncate">
            {getAttachmentLabel(attachment)}
          </span>
        </div>
        <video controls className="w-full rounded-md max-h-32">
          <source src={previewSource} type={attachment.mimeType || 'video/mp4'} />
          Your browser does not support video playback.
        </video>
        {renderRemoveButton(attachment.id)}
      </div>
    );
  };

  const renderAttachment = (attachment) => {
    switch (attachment.type) {
      case 'image':
        return renderImageAttachment(attachment);
      case 'audio':
        return renderAudioAttachment(attachment);
      case 'video':
        return renderVideoAttachment(attachment);
      case 'pdf':
      default:
        return renderPdfAttachment(attachment);
    }
  };

  if (embedded) {
    return (
      <>
        {attachments.map((attachment) => renderAttachment(attachment))}
      </>
    );
  }

  return (
    <div className="mb-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {attachments.length} attachment{attachments.length > 1 ? 's' : ''}
        </span>
        {onClearAll && (
          <button
            type="button"
            onClick={onClearAll}
            className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
          >
            Clear all
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-3">
        {attachments.map((attachment) => renderAttachment(attachment))}
      </div>
    </div>
  );
};

export default FilePreview;
